/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

// Import the store and context
import { useHQVStore, HQVContextType, HQVStore } from '../../modules/store/hqvZustandStore';
import { HQVContext } from '../../context/HQVContext';

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 *
 * Uses feature flags: EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND and EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND
 * Checks BOTH flags and uses Zustand if EITHER is enabled (works for both SERP and ADF pages)
 *
 * Note: The Zustand store is updated proactively in provider components
 * (NGSearchForm and SearchForm) rather than reactively in this hook.
 * This is more efficient and follows the principle of updating at the source.
 */
export const useConditionalHQVContext = (): HQVContextType & Partial<Omit<HQVStore, keyof HQVContextType>> => {
  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Check BOTH flags - use Zustand if either is enabled
  // This allows both SERP and ADF pages to work automatically
  const shouldUseZustand =
    (featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false) ||
    (featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false);

  // Always call hooks at top level (Rules of Hooks)
  const contextValue = useContext(HQVContext);

  // Only subscribe to STATE using a selector (for reactivity)
  const hqvState = useHQVStore(state => state.hqvState);

  // Get methods using selectors (methods are stable, won't cause re-renders)
  const hqvDispatch = useHQVStore(state => state.hqvDispatch);
  const setAmenetiesAvailable = useHQVStore(state => state.setAmenetiesAvailable);
  const setPhotosAvailable = useHQVStore(state => state.setPhotosAvailable);
  const setPropertyDetailsAvailable = useHQVStore(state => state.setPropertyDetailsAvailable);
  const setInformationAvailable = useHQVStore(state => state.setInformationAvailable);
  const setPropertyID = useHQVStore(state => state.setPropertyID);
  const setModalOpenState = useHQVStore(state => state.setModalOpenState);
  const setOpenGalleryOnLoad = useHQVStore(state => state.setOpenGalleryOnLoad);
  const setPropertySessionData = useHQVStore(state => state.setPropertySessionData);
  const setPropertyDistance = useHQVStore(state => state.setPropertyDistance);
  const setCachedPropertyMedia = useHQVStore(state => state.setCachedPropertyMedia);

  if (shouldUseZustand) {
    // Return Zustand store data with all direct methods
    return {
      hqvState, // Reactive state from selector
      hqvDispatch,
      setAmenetiesAvailable,
      setPhotosAvailable,
      setPropertyDetailsAvailable,
      setInformationAvailable,
      setPropertyID,
      setModalOpenState,
      setOpenGalleryOnLoad,
      setPropertySessionData,
      setPropertyDistance,
      setCachedPropertyMedia,
    };
  } else {
    // Return React Context data (only has hqvState and hqvDispatch)
    return contextValue;
  }
};
